import React, { useEffect, useRef, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { COMENTARIO_MIN_POR_OMISION } from '../types/metas.types';
import { largoComentario } from './ReabrirControl';
import { CLASE_BOTON, CLASE_BOTON_TEXTO, CLASE_OVERLAY, cx } from './ui/clases';

interface Props {
  /** La tienda que se devuelve («VAZ 56 · XOCHIMILCO»); `null` = cerrado. */
  tienda: string | null;
  /** `mi-alcance.parametros.comentarioMin` (10 por omisión): el mismo mínimo que exige la API. */
  comentarioMin?: number;
  /** Diálogo a pantalla completa (teléfono). */
  movil?: boolean;
  onHide: () => void;
  /**
   * Devuelve la tienda con el comentario. `true` = se devolvió (el diálogo se
   * cierra); `false` = no (el error lo dice `error` o la pantalla).
   */
  onConfirmar: (comentario: string) => Promise<boolean>;
  /** Lo que respondió la API al devolver (422 por el comentario, 409…), dentro del diálogo. */
  error?: string | null;
}

/**
 * «Devolver al gerente…» (21-oct-2026, decisión del usuario, fase B; diseño
 * `Supervisor-revision`). El supervisor regresa al gerente la captura que le
 * mandó («con el supervisor» → borrador) con un comentario OBLIGATORIO de al
 * menos `comentarioMin` caracteres: el gerente lo lee como aviso en la app de
 * la tienda y es lo único que le dice qué corregir. Si falta, el error sale EN
 * EL CAMPO y no se manda nada (un botón deshabilitado no dice por qué).
 */
export const DevolverDialog: React.FC<Props> = ({
  tienda,
  comentarioMin = COMENTARIO_MIN_POR_OMISION,
  movil = false,
  onHide,
  onConfirmar,
  error,
}) => {
  const minimo = Math.max(1, comentarioMin);
  const [comentario, setComentario] = useState('');
  const [errorCampo, setErrorCampo] = useState<string | null>(null);
  const [trabajando, setTrabajando] = useState(false);
  const campo = useRef<HTMLTextAreaElement>(null);
  const largo = largoComentario(comentario);

  // Cada vez que se abre, en blanco.
  useEffect(() => {
    if (!tienda) return;
    setComentario('');
    setErrorCampo(null);
    setTrabajando(false);
  }, [tienda]);

  const confirmar = async () => {
    if (trabajando) return;
    if (largo < minimo) {
      setErrorCampo(
        largo === 0
          ? `Escribe qué debe corregir el gerente (al menos ${minimo} caracteres).`
          : `Escribe al menos ${minimo} caracteres para que el gerente sepa qué corregir (llevas ${largo}).`,
      );
      campo.current?.focus();
      return;
    }
    setTrabajando(true);
    try {
      if (await onConfirmar(comentario.trim())) onHide();
    } finally {
      setTrabajando(false);
    }
  };

  const pie = (
    <div className="flex flex-wrap justify-end gap-2">
      <button type="button" className={CLASE_BOTON_TEXTO} onClick={onHide} disabled={trabajando}>
        Cancelar
      </button>
      <button
        type="button"
        className={cx(CLASE_BOTON, 'border-metas-error-borde !text-metas-error')}
        onClick={() => void confirmar()}
        disabled={trabajando}
      >
        {trabajando && <i className="pi pi-spin pi-spinner" aria-hidden="true" />}
        Devolver al gerente
      </button>
    </div>
  );

  return (
    <Dialog
      // La ✕ en español: el locale global de PrimeReact sigue en `en` («Close»).
      ariaCloseIconLabel="Cerrar"
      className={CLASE_OVERLAY}
      header={tienda ? `Devolver al gerente · ${tienda}` : 'Devolver al gerente'}
      visible={!!tienda}
      onHide={() => {
        if (!trabajando) onHide();
      }}
      footer={pie}
      maximized={movil}
      onMaximize={() => undefined}
      style={movil ? { width: '100vw', maxWidth: '100vw', margin: 0 } : { width: '34rem', maxWidth: '95vw' }}
      modal
    >
      <div className="flex flex-col gap-3" data-testid="devolver-dialog">
        <p className="m-0 text-sm text-metas-texto-2">
          Vuelve a la captura del gerente: la ve en la app de la tienda con tu comentario, la corrige y te la vuelve a
          enviar. Tus ajustes se quedan.
        </p>
        <label htmlFor="metas-comentario-devolver" className="text-metas-label text-metas-texto-3">
          Qué debe corregir <span className="font-normal text-metas-texto-2">(obligatorio)</span>
        </label>
        <textarea
          id="metas-comentario-devolver"
          ref={campo}
          rows={4}
          maxLength={1000}
          value={comentario}
          onChange={(e) => {
            setComentario(e.target.value);
            if (errorCampo && largoComentario(e.target.value) >= minimo) setErrorCampo(null);
          }}
          placeholder="Ej. Sube la meta de Luis a su venta promedio y quita la incapacidad de Ana."
          aria-invalid={errorCampo ? true : undefined}
          aria-describedby={errorCampo ? 'metas-comentario-devolver-error' : 'metas-comentario-devolver-contador'}
          className={cx(
            'metas-foco resize-none rounded-metas-control border bg-metas-panel px-3 py-2.5 leading-[1.45] text-metas-texto',
            movil ? 'text-base' : 'text-sm',
            errorCampo ? 'border-metas-error' : 'border-metas-borde-control',
          )}
        />
        {errorCampo ? (
          <span id="metas-comentario-devolver-error" role="alert" className="text-xs font-semibold text-metas-error">
            {errorCampo}
          </span>
        ) : (
          <span id="metas-comentario-devolver-contador" className="text-xs text-metas-texto-2" data-testid="contador-devolver">
            {largo} / {minimo} caracteres como mínimo
          </span>
        )}
        {error && (
          <p role="alert" className="m-0 rounded-metas-control border border-metas-error bg-metas-error-fondo px-3 py-2 text-sm font-semibold text-metas-error">
            {error}
          </p>
        )}
      </div>
    </Dialog>
  );
};

export default DevolverDialog;
